import React from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownV2 } from 'components/form';
import { Row } from 'components/layout';
import { QuestionState } from 'lib/models/answers';
import styles from './TouchpointQuestions.module.scss';

export interface TouchpointQuestionsProps {
  isSubjectRequiredState?: QuestionState;
  setSubjectRequiredState?: (value: QuestionState) => void;
  isCVRequiredState: QuestionState;
  setCVRequiredState: (value: QuestionState) => void;
  isLinkedInProfileRequiredState: QuestionState;
  setLinkedInProfileRequiredState: (value: QuestionState) => void;
  isUniversityRequiredState?: QuestionState;
  setUniversityRequiredState?: (value: QuestionState) => void;
  customQuestionsList: Array<{
    id?: string;
    title: string;
    state: QuestionState;
    isNew?: boolean;
    options?: Array<string>;
    question_type?: string;
  }>;
  isStudyYearRequiredState?: QuestionState;
  setStudyYearRequiredState?: (value: QuestionState) => void;
  isDegreeRequiredState?: QuestionState;
  setDegreeRequiredState?: (value: QuestionState) => void;
  QuestionOptions: Array<{ id: string; label: string; value: string }>;
  onChangeCustomQuesState: (questionId: string, state: QuestionState) => void;
  defaultQuestionOptions: Array<{ id: string; label: string; value: string }>;
  dropDownClassName: string;
  questionListClassName: string;
  customQuesClassName: string;
  isEventPostingFlow?: boolean;
  triggerButtonClassName?: string;
  isDefaultTriggerClass?: boolean;
}

export const TouchpointQuestions: React.FC<TouchpointQuestionsProps> = ({
  isSubjectRequiredState,
  setSubjectRequiredState,
  isCVRequiredState,
  setCVRequiredState,
  isLinkedInProfileRequiredState,
  setLinkedInProfileRequiredState,
  isUniversityRequiredState,
  setUniversityRequiredState,
  customQuestionsList,
  isStudyYearRequiredState,
  setStudyYearRequiredState,
  isDegreeRequiredState,
  setDegreeRequiredState,
  QuestionOptions,
  onChangeCustomQuesState,
  defaultQuestionOptions,
  dropDownClassName,
  questionListClassName,
  customQuesClassName,
  isEventPostingFlow = true,
  triggerButtonClassName,
  isDefaultTriggerClass,
}: TouchpointQuestionsProps) => {
  const [t] = useTranslation('opportunity.posting');
  return (
    <div>
      <div className={questionListClassName}>
        {isEventPostingFlow ? (
          <>
            <Row align="center" justify="space-between" wrap={false}>
              <p>{t('touchpoint-questions.university')}</p>

              <DropdownV2
                value={isUniversityRequiredState || ''}
                options={defaultQuestionOptions}
                onChange={(value: string) => {
                  setUniversityRequiredState &&
                    setUniversityRequiredState(value as QuestionState);
                }}
                label={''}
                selectedValueLabel={isUniversityRequiredState || ''}
                isSearchable={false}
                isValid={true}
                className={dropDownClassName}
                hasBorder={false}
              />
            </Row>
            <hr className={styles.separator}></hr>
            <Row align="center" justify="space-between" wrap={false}>
              <p>{t('touchpoint-questions.graduation-year')}</p>
              <DropdownV2
                value={isStudyYearRequiredState || ''}
                options={defaultQuestionOptions}
                onChange={(value: string) => {
                  setStudyYearRequiredState &&
                    setStudyYearRequiredState(value as QuestionState);
                }}
                label={''}
                selectedValueLabel={isStudyYearRequiredState || ''}
                isSearchable={false}
                isValid={true}
                className={dropDownClassName}
                hasBorder={false}
              />
            </Row>
            <hr className={styles.separator}></hr>
            <Row align="center" justify="space-between" wrap={false}>
              <p>{t('touchpoint-questions.degree-type')}</p>

              <DropdownV2
                value={isDegreeRequiredState || ''}
                options={defaultQuestionOptions}
                onChange={(value: string) => {
                  setDegreeRequiredState &&
                    setDegreeRequiredState(value as QuestionState);
                }}
                label={''}
                selectedValueLabel={isDegreeRequiredState || ''}
                isSearchable={false}
                isValid={true}
                className={dropDownClassName}
                hasBorder={false}
              />
            </Row>
            <hr className={styles.separator}></hr>
            <Row align="center" justify="space-between" wrap={false}>
              <p>{t('touchpoint-questions.subject-of-study')}</p>

              <DropdownV2
                value={isSubjectRequiredState || ''}
                options={defaultQuestionOptions}
                onChange={(value: string) => {
                  setSubjectRequiredState &&
                    setSubjectRequiredState(value as QuestionState);
                }}
                label={''}
                selectedValueLabel={isSubjectRequiredState || ''}
                isSearchable={false}
                isValid={true}
                className={dropDownClassName}
                hasBorder={false}
              />
            </Row>
            <hr className={styles.separator}></hr>
          </>
        ) : null}
        <Row align="center" justify="space-between" wrap={false}>
          <p>{t('touchpoint-questions.cv')}</p>

          <DropdownV2
            value={isCVRequiredState}
            options={defaultQuestionOptions}
            onChange={(value: string) => {
              setCVRequiredState(value as QuestionState);
            }}
            label={''}
            selectedValueLabel={isCVRequiredState}
            isSearchable={false}
            isValid={true}
            className={dropDownClassName}
            hasBorder={false}
            triggerButtonClassName={triggerButtonClassName}
            isDefaultTriggerClass={isDefaultTriggerClass}
          />
        </Row>
        <hr className={styles.separator}></hr>
        <Row align="center" justify="space-between" wrap={false}>
          <p>{t('touchpoint-questions.linkedin-profile')}</p>

          <DropdownV2
            value={isLinkedInProfileRequiredState}
            options={defaultQuestionOptions}
            onChange={(value: string) => {
              setLinkedInProfileRequiredState(value as QuestionState);
            }}
            label={''}
            selectedValueLabel={isLinkedInProfileRequiredState}
            isSearchable={false}
            isValid={true}
            className={dropDownClassName}
            hasBorder={false}
            triggerButtonClassName={triggerButtonClassName}
            isDefaultTriggerClass={isDefaultTriggerClass}
          />
        </Row>
      </div>
      {customQuestionsList.map(({ id, title, state }) => {
        return (
          <div key={id}>
            <hr className={styles.separator}></hr>
            <Row
              align="center"
              justify="space-between"
              wrap={false}
              className={customQuesClassName}
            >
              <p className={styles.questionTitle}>{title}</p>
              <DropdownV2
                value={state}
                options={QuestionOptions}
                onChange={(value: string) => {
                  onChangeCustomQuesState(id || '', value as QuestionState);
                }}
                label={''}
                selectedValueLabel={state}
                isSearchable={false}
                isValid={true}
                className={dropDownClassName}
                hasBorder={false}
                triggerButtonClassName={triggerButtonClassName}
                isDefaultTriggerClass={isDefaultTriggerClass}
              />
            </Row>
          </div>
        );
      })}
    </div>
  );
};

TouchpointQuestions.displayName = 'TouchpointQuestions';
